import { useRef, useState } from 'react';

import type { HorizontalScrollModel } from '@app/shared/hooks';

import {
  getFrameGrabOffset,
  getMinimapScale,
  getScrollLeftFromPointer,
  toMinimapPx,
} from '../lib/getMinimapGeometry';

import type { PointerEvent as ReactPointerEvent } from 'react';

import './ScrollMinimap.scss';

// A scaled column primitive: just a position and width. Blocks are rendered in
// a single neutral tone so a dense board reads as a calm minimap, not a stripe
// of colors. No content preview — primitives only.
export interface MinimapColumn {
  id: string | number;
  offset: number;
  width: number;
}

export interface ScrollMinimapProps {
  model: HorizontalScrollModel;
  columns: MinimapColumn[];
  width?: number;
  className?: string;
}

const DEFAULT_MINIMAP_WIDTH_PX = 200;
const MIN_COLUMN_WIDTH_PX = 1;

export const ScrollMinimap = ({
  model,
  columns,
  width = DEFAULT_MINIMAP_WIDTH_PX,
  className,
}: ScrollMinimapProps) => {
  const { contentWidth, viewportWidth, scrollLeft, onScrollTo } = model;

  const trackRef = useRef<HTMLDivElement | null>(null);
  const grabOffsetRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const maxScrollLeft = Math.max(0, contentWidth - viewportWidth);

  // Nothing to navigate: don't render a control that cannot move.
  if (maxScrollLeft <= 0) {
    return null;
  }

  const scale = getMinimapScale(contentWidth, width);
  const frameLeft = toMinimapPx(scrollLeft, scale);
  const frameWidth = toMinimapPx(viewportWidth, scale);

  const clamp = (value: number): number =>
    Math.min(Math.max(value, 0), maxScrollLeft);

  const getPointerX = (clientX: number): number => {
    const rect = trackRef.current?.getBoundingClientRect();

    return rect ? clientX - rect.left : 0;
  };

  const scrollToPointer = (clientX: number): void => {
    const pointerX = getPointerX(clientX);

    onScrollTo(
      clamp(getScrollLeftFromPointer(pointerX, grabOffsetRef.current, scale))
    );
  };

  const pointerDownHandler = (
    event: ReactPointerEvent<HTMLDivElement>
  ): void => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    grabOffsetRef.current = getFrameGrabOffset({
      pointerX: getPointerX(event.clientX),
      frameLeft,
      frameWidth,
    });

    scrollToPointer(event.clientX);
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsDragging(true);
  };

  const pointerMoveHandler = (
    event: ReactPointerEvent<HTMLDivElement>
  ): void => {
    if (!isDragging) {
      return;
    }

    scrollToPointer(event.clientX);
  };

  const pointerUpHandler = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    setIsDragging(false);
  };

  return (
    <div
      className={['ScrollMinimap', className].filter(Boolean).join(' ')}
      style={{ width }}
    >
      <div
        className="ScrollMinimap__track"
        ref={trackRef}
        onPointerDown={pointerDownHandler}
        onPointerMove={pointerMoveHandler}
        onPointerUp={pointerUpHandler}
        onPointerCancel={pointerUpHandler}
      >
        {columns.map((column) => (
          <span
            key={column.id}
            className="ScrollMinimap__column"
            style={{
              left: toMinimapPx(column.offset, scale),
              width: Math.max(
                MIN_COLUMN_WIDTH_PX,
                toMinimapPx(column.width, scale)
              ),
            }}
          />
        ))}
        <span
          className={[
            'ScrollMinimap__viewport',
            isDragging && 'ScrollMinimap__viewport--dragging',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{ left: frameLeft, width: frameWidth }}
        />
      </div>
    </div>
  );
};
